import {
  CreateUpdateSimpleUserDto,
  DeactivateUserGroupDto,
  InviteUserGroupDto,
  UpdateCurrentUserDto,
  UpdateCurrentUserPasswordDto,
  UpdateLanguageDto,
  UpdateTimezoneDto,
  UpdateUserFolderAccessGroupDto,
  UpdateUserRoleGroupDto,
  UserFolderAccessStateDto,
} from '@geartracker/dto';
import type {
  AutorizedRole,
  BaseFolder,
  OnDemandUserDocumentType,
  User,
  UserDocument,
} from '@geartracker/types';

import { useSnackbar } from 'src/components/snackbar';
import { useTranslate } from 'src/locales/use-locales';
import axios, { endpoints } from 'src/utils/axios';
import {
  getErrorMessage,
  rethrowWithBlobErrorMessage,
} from 'src/utils/error-message';
import { assertDtoShape } from 'src/utils/validate-dto';
import { equipmentKeys } from './equipment.api';
import { eventKeys } from './events.api';
import { folderKeys } from './folders.api';
import { statsKeys } from './stats.api';

import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export type FolderAccessEntry = {
  id: number;
  name: string;
  description: string | null;
  isAccessibleByOrganization: boolean;
  depth: number;
  path: string;
  parentId: number | null;
  parent?: BaseFolder | null;
  children: BaseFolder[];
  /**
   * 'none' is the only "no access" answer: 'inherited' means the employee
   * reaches this org-wide folder through a restricted ancestor, which is also
   * the only place that access can be changed.
   */
  accessType: 'organization' | 'explicit' | 'inherited' | 'none';
  _count: { Equipment: number };
};

export type UserFolderAccessResponse = {
  isAdmin: boolean;
  folders: FolderAccessEntry[];
};

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (type: string) => [...userKeys.lists(), { type }] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: number) => [...userKeys.details(), id] as const,
  me: () => [...userKeys.details(), 'me'] as const,
  meDocuments: () => [...userKeys.me(), 'documents'] as const,
  folderAccess: (id: number) =>
    [...userKeys.detail(id), 'folder-access'] as const,
  folderAccessState: (ids: number[]) =>
    [...userKeys.all, 'folder-access-state', ids] as const,
  folderAccessUserIdsAll: () =>
    [...userKeys.all, 'folder-access-user-ids'] as const,
  folderAccessUserIds: (folderId: number | null) =>
    [...userKeys.folderAccessUserIdsAll(), folderId] as const,
  documents: (id: number) => [...userKeys.detail(id), 'documents'] as const,
};

/**
 * Current user as returned by GET /users/me.
 * The endpoint intentionally strips `Organization` (org data is fetched
 * separately via `useOrganization`), so it is not available here.
 */
export type CurrentUser = Omit<User, 'Organization'>;

/**
 * Get Current User
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await axios.get(endpoints.users.me.root);
  return response.data;
}

export const useCurrentUser = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: getCurrentUser,
    enabled,
  });
};

/**
 * Update current user
 */
const updateCurentUser = async (user: UpdateCurrentUserDto): Promise<User> => {
  const response = await axios.patch(
    endpoints.users.me.root,
    assertDtoShape(UpdateCurrentUserDto, user, 'updateCurentUser'),
  );
  return response.data;
};

export const useUpdateCurrentUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.update');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCurentUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Current user avatar.
 * The server owns the stored path, so these endpoints take/return only the
 * image itself — the avatar is never settable through the user update payload.
 */
export type CurrentUserAvatar = { avatar: string | null };

const uploadCurrentUserAvatar = async (
  file: File,
): Promise<CurrentUserAvatar> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(endpoints.users.me.avatar, formData);
  return response.data;
};

const deleteCurrentUserAvatar = async (): Promise<CurrentUserAvatar> => {
  const response = await axios.delete(endpoints.users.me.avatar);
  return response.data;
};

const useCurrentUserAvatarMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<CurrentUserAvatar>,
  successKey: 'success' | 'removed',
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.avatar');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      enqueueSnackbar(t(successKey));
      // Avatars are embedded in equipment, transfer, inspection, training and
      // medical-visit payloads too, and the server deletes the previous R2
      // object right away — any cache still holding the old path would render a
      // dead image. Changing an avatar is rare enough to just refetch all.
      void queryClient.invalidateQueries();
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

export const useUploadCurrentUserAvatar = () =>
  useCurrentUserAvatarMutation(uploadCurrentUserAvatar, 'success');

// <void> is explicit: a zero-arg mutationFn infers TVariables as unknown, which
// would force callers to pass a dummy argument to mutate()
export const useDeleteCurrentUserAvatar = () =>
  useCurrentUserAvatarMutation<void>(deleteCurrentUserAvatar, 'removed');

/**
 * Update current user language
 */
const updateCurentUserLanguage = async (
  data: UpdateLanguageDto,
): Promise<void> => {
  await axios.post(
    endpoints.users.me.language,
    assertDtoShape(UpdateLanguageDto, data, 'updateCurentUserLanguage'),
  );
};

export const useUpdateCurrentUserLanguage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.update');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCurentUserLanguage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Report the browser's time zone.
 *
 * Silent on both ends: nobody asked for this, so a success needs no snackbar
 * and a failure must not raise one either — the server falls back to a default
 * zone, which is exactly where we already were.
 */
const updateCurrentUserTimezone = async (
  data: UpdateTimezoneDto,
): Promise<void> => {
  await axios.post(
    endpoints.users.me.timezone,
    assertDtoShape(UpdateTimezoneDto, data, 'updateCurrentUserTimezone'),
  );
};

export const useUpdateCurrentUserTimezone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCurrentUserTimezone,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
};

/**
 *
 * Change current user password
 */
const changeCurrentUserPassword = async (
  data: UpdateCurrentUserPasswordDto,
): Promise<void> => {
  await axios.post(
    endpoints.users.me.changePassword,
    assertDtoShape(
      UpdateCurrentUserPasswordDto,
      data,
      'changeCurrentUserPassword',
    ),
  );
};

export const useChangeCurrentUserPassword = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.updatePassword');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeCurrentUserPassword,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Delete own user account
 */
const deleteOwnUser = async (): Promise<boolean> => {
  const response = await axios.post(endpoints.users.me.deleteAccount);
  return response.data;
};

export const useDeleteOwnUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.delete');

  return useMutation({
    mutationFn: deleteOwnUser,
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Get User
 */
export async function getUser(id: number): Promise<User> {
  const response = await axios.get(endpoints.users.root(id));
  return response.data;
}

export const useUser = (id: number, enabled = true) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => getUser(id),
    enabled,
  });
};

/**
 * Update user
 */
const updateUser = async ({
  userId,
  user,
}: {
  userId: number;
  user: CreateUpdateSimpleUserDto;
}): Promise<User> => {
  const response = await axios.patch(
    endpoints.users.root(userId),
    assertDtoShape(CreateUpdateSimpleUserDto, user, 'updateUser'),
  );
  return response.data;
};

export const useUpdateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.update');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.folderAccess(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: userKeys.folderAccessUserIdsAll(),
      });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Get all users
 *
 */
export async function getAllUsers(): Promise<User[]> {
  const response = await axios.get(endpoints.users.root());
  return response.data;
}

export const useAllUsers = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.list('all'),
    queryFn: getAllUsers,
    enabled,
  });
};

/**
 * Ids of the users who reach a folder.
 *
 * The users list filters by folder against this rather than against grants
 * carried by each row: the grants are users × folders, this is one array, and
 * it is only fetched while a folder is picked. Admins are already in it — the
 * server adds them, they reach every folder by role without holding a grant.
 */
export async function getUserIdsWithFolderAccess(
  folderId: number,
): Promise<number[]> {
  const response = await axios.get(
    endpoints.users.folderAccessUserIds(folderId),
  );
  return response.data;
}

export const useUserIdsWithFolderAccess = (folderId: number | null) => {
  return useQuery({
    queryKey: userKeys.folderAccessUserIds(folderId),
    // No folder picked: nothing to ask for, and no `enabled` flag needed —
    // `skipToken` says it in a way that keeps `folderId` non-null below.
    queryFn:
      folderId === null
        ? skipToken
        : () => getUserIdsWithFolderAccess(folderId),
  });
};

/**
 * Get all available users
 */
export async function getAllAvailableUsers(): Promise<User[]> {
  const response = await axios.get(endpoints.users.available);
  return response.data;
}

export const useAllAvailableUsers = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.list('available'),
    queryFn: getAllAvailableUsers,
    enabled,
  });
};

/**
 * Create user
 */
const createUser = async (user: CreateUpdateSimpleUserDto): Promise<User> => {
  const response = await axios.post(
    endpoints.users.root(),
    assertDtoShape(CreateUpdateSimpleUserDto, user, 'createUser'),
  );
  return response.data;
};

export const useCreateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.create');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: statsKeys.all });
      void queryClient.invalidateQueries({
        queryKey: userKeys.folderAccess(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: userKeys.folderAccessUserIdsAll(),
      });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Deactivate user
 */
const deactivateUser = async (id: number): Promise<User> => {
  const response = await axios.post(endpoints.users.deactivate(id));
  return response.data;
};

export const useDeactivateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.deactivate');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
      // Because deactivaed users are unlinked to their equipment,
      //we need to invalidate ALL the equipment,
      //we cannot predict which equipment is linked to the user
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

export type DeactivateUserGroupResult = {
  deactivatedUserIds: number[];
  skippedUserIds: number[];
};

const deactivateUserGroup = async (
  userIds: number[],
): Promise<DeactivateUserGroupResult> => {
  const response = await axios.post(
    endpoints.users.deactivateGroup,
    assertDtoShape(DeactivateUserGroupDto, { userIds }, 'deactivateUserGroup'),
  );
  return response.data;
};

export const useDeactivateUserGroup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.deactivate');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateUserGroup,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      for (const id of data.deactivatedUserIds) {
        void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      }
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Reactivate user
 */
const reactivateUser = async (id: number): Promise<User> => {
  const response = await axios.post(endpoints.users.reactivate(id));
  return response.data;
};

export const useReactivateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.reactivate');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reactivateUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Invite again user
 */
const inviteUser = async (id: number): Promise<User> => {
  const response = await axios.post(endpoints.users.invite(id));
  return response.data;
};

export const useInviteUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.invite');

  return useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Get user folder access
 */
const getUserFolderAccess = async (
  userId: number,
): Promise<UserFolderAccessResponse> => {
  const response = await axios.get(endpoints.users.folderAccess(userId));
  return response.data;
};

export const useUserFolderAccess = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: userKeys.folderAccess(userId),
    queryFn: () => getUserFolderAccess(userId),
    enabled,
  });
};

/**
 * Get the documents concerning a user (e.g. transfer certificates
 * where they were the recipient)
 */
const getUserDocuments = async (userId: number): Promise<UserDocument[]> => {
  const response = await axios.get(endpoints.users.documents(userId));
  return response.data;
};

const getCurrentUserDocuments = async (): Promise<UserDocument[]> => {
  const response = await axios.get(endpoints.users.me.documents);
  return response.data;
};

export const useCurrentUserDocuments = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.meDocuments(),
    queryFn: getCurrentUserDocuments,
    enabled,
  });
};

export const useUserDocuments = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: userKeys.documents(userId),
    queryFn: () => getUserDocuments(userId),
    enabled,
  });
};

/**
 * Get a short-lived download URL for any document in a user's "Documents"
 * tab, regardless of its documentType (transfer certificate, medical visit
 * attestation, ...).
 */
export const getUserDocumentDownloadUrl = async (
  userId: number,
  documentId: number,
): Promise<string> => {
  const response = await axios.get<{ url: string }>(
    endpoints.users.documentDownload(userId, documentId),
  );
  return response.data.url;
};

export const getCurrentUserDocumentDownloadUrl = async (
  documentId: number,
): Promise<string> => {
  const response = await axios.get<{ url: string }>(
    endpoints.users.me.documentDownload(documentId),
  );
  return response.data.url;
};

/**
 * Renders a compliance document without filing it, so the operator can check
 * it before it lands in the user's record. Fetched through axios rather than
 * pointed at by an `<iframe src>`: the API is Bearer-authenticated, and a
 * bare iframe request would carry no Authorization header.
 *
 * A POST even though nothing is stored: the route refreshes the target's
 * compliance statuses before rendering, so it is not the replay-safe read a
 * GET would announce.
 */
const previewUserDocument = async (
  userId: number,
  documentType: OnDemandUserDocumentType,
): Promise<Blob> => {
  try {
    const response = await axios.post<Blob>(
      endpoints.users.documentsPreview(userId),
      { documentType },
      { responseType: 'blob' },
    );
    return response.data;
  } catch (error) {
    return rethrowWithBlobErrorMessage(error);
  }
};

/**
 * A mutation, not a query: TanStack's cache outlives the component, so a Blob
 * (and any object URL made from it) would sit there with nobody left to revoke
 * it. Mutation state dies with the dialog that owns it.
 */
export const usePreviewUserDocument = (userId: number) =>
  useMutation({
    mutationFn: (documentType: OnDemandUserDocumentType) =>
      previewUserDocument(userId, documentType),
  });

/**
 * Where the backend's document errors are translated. Those errors are keys,
 * not sentences — `Document-no-habilitation` & co. — so the dialog reads them
 * from the same namespace as the snackbar: one wording per cause, wherever it
 * surfaces.
 */
export const USER_DOCUMENT_ERROR_NS = 'api.user.generateDocument';

const generateUserDocument = async (
  userId: number,
  documentType: OnDemandUserDocumentType,
): Promise<UserDocument> => {
  const response = await axios.post(endpoints.users.documents(userId), {
    documentType,
  });
  return response.data;
};

export const useGenerateUserDocument = (userId: number) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(USER_DOCUMENT_ERROR_NS);

  return useMutation({
    mutationFn: (documentType: OnDemandUserDocumentType) =>
      generateUserDocument(userId, documentType),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.documents(userId),
      });
      // Unconditional: on someone else's profile that query is inactive, so
      // invalidating it costs nothing — cheaper than branching on "is this me".
      void queryClient.invalidateQueries({ queryKey: userKeys.meDocuments() });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

export type InviteUserGroupResult = {
  invitedUserIds: number[];
  skippedUserIds: number[];
};

const inviteUserGroup = async (
  userIds: number[],
): Promise<InviteUserGroupResult> => {
  const response = await axios.post(
    endpoints.users.inviteGroup,
    assertDtoShape(InviteUserGroupDto, { userIds }, 'inviteUserGroup'),
  );
  return response.data;
};

export const useInviteUserGroup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.invite');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteUserGroup,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

export type UpdateUserRoleGroupResult = {
  updatedUserIds: number[];
  skippedUserIds: number[];
};

const updateUserRoleGroup = async (input: {
  userIds: number[];
  /** Never `SUPERADMIN`: the DTO only accepts the roles an org can hand out. */
  role: AutorizedRole;
}): Promise<UpdateUserRoleGroupResult> => {
  const response = await axios.post(
    endpoints.users.roleGroup,
    assertDtoShape(UpdateUserRoleGroupDto, input, 'updateUserRoleGroup'),
  );
  return response.data;
};

export const useUpdateUserRoleGroup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.roleGroup');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserRoleGroup,
    onSuccess: (result) => {
      // The count comes from the server, never from the selection: rows can be
      // skipped (an admin, no email, out of a manager's scope), and reporting
      // the requested number would claim changes that did not happen.
      //
      // No second snackbar for those: the dialog lists what gets skipped
      // before the click, which is when it is still actionable. A warning
      // afterwards reads as a failure over a deliberate no-op.
      enqueueSnackbar(t('success', { count: result.updatedUserIds.length }));
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * The folder tree annotated for a whole selection: each folder is assigned to
 * all, some, or none of the selected employees. `partial` is the state the
 * bulk editor exists to show — see UserFolderPermissions.
 */
export type UsersFolderAccessStateResponse = {
  adminUserIds: number[];
  countedUserCount: number;
  folders: (Omit<FolderAccessEntry, 'accessType'> & {
    accessType: 'organization' | 'explicit' | 'partial' | 'none';
    assignedCount: number;
  })[];
};

const getUsersFolderAccessState = async (
  userIds: number[],
): Promise<UsersFolderAccessStateResponse> => {
  const response = await axios.post(
    endpoints.users.folderAccessState,
    assertDtoShape(
      UserFolderAccessStateDto,
      { userIds },
      'getUsersFolderAccessState',
    ),
  );
  return response.data;
};

/**
 * A query behind a POST: the selection can hold hundreds of ids, too many for
 * a query string. Keyed on the sorted selection so reopening the dialog on the
 * same rows is a cache hit, and on different rows never is.
 */
export const useUsersFolderAccessState = (
  userIds: number[],
  enabled = true,
) => {
  const sortedIds = [...userIds].sort((a, b) => a - b);

  return useQuery({
    queryKey: userKeys.folderAccessState(sortedIds),
    queryFn: () => getUsersFolderAccessState(sortedIds),
    enabled: enabled && sortedIds.length > 0,
  });
};

const updateUserFolderAccessGroup = async (input: {
  userIds: number[];
  addFolderIds: number[];
  removeFolderIds: number[];
}): Promise<UpdateUserRoleGroupResult> => {
  const response = await axios.post(
    endpoints.users.folderAccessGroup,
    assertDtoShape(
      UpdateUserFolderAccessGroupDto,
      input,
      'updateUserFolderAccessGroup',
    ),
  );
  return response.data;
};

export const useUpdateUserFolderAccessGroup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.folderAccessGroup');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserFolderAccessGroup,
    onSuccess: (result) => {
      enqueueSnackbar(t('success', { count: result.updatedUserIds.length }));
      // No warning about skipped users, unlike the role change: here they are
      // the admins of the selection, who reach every folder by role, and the
      // dialog already said so before the click. Repeating it as a warning
      // reads as "something went wrong" about a no-op.
      // Folder access feeds both the users list and every per-user folder tab,
      // so the whole user namespace is stale — as is the folder tree, which
      // counts the users it grants access to.
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      void queryClient.invalidateQueries({ queryKey: folderKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};
